/**
 * Customer Portal (UI) - campaigns list and detail views.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TemplateAssignmentModal } from '../components/templates/TemplateAssignmentModal';
import { fetchCampaigns, fetchPhoneNumbers } from '../features/api/client';
import type { Campaign, PhoneNumber, ScopeType } from '../types/portal';

type AssignmentTarget = {
  scopeType: ScopeType;
  scopeId: string;
  campaignId?: string | null;
  title: string;
};

type CampaignsRouteProps = {
  accountId: string | null;
};

export function CampaignsRoute({ accountId }: CampaignsRouteProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCampaigns(accountId)
      .then((response) => {
        if (cancelled) return;
        setCampaigns(response.items);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return (
    <>
      <div className="page-pretitle">Messaging</div>
      <h2 className="page-title mb-3">Campaigns</h2>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? <div className="text-secondary">Loading campaigns...</div> : null}

      {!loading ? (
        <div className="row row-cards">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="col-sm-6 col-lg-4">
              <Link to={`/campaigns/${encodeURIComponent(campaign.id)}`} className="card card-link cursor-pointer">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <h3 className="card-title mb-1">{campaign.name}</h3>
                      <div className="text-secondary small">{campaign.id}</div>
                    </div>
                    <span className="badge bg-orange-lt text-orange">View</span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
          {!campaigns.length ? (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-secondary">No campaigns found for your account.</div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

type CampaignDetailRouteProps = {
  accountId: string | null;
};

export function CampaignDetailRoute({ accountId }: CampaignDetailRouteProps) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const decodedCampaignId = campaignId ? decodeURIComponent(campaignId) : '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'numbers'>('details');
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<PhoneNumber | null>(null);
  const [assignmentTarget, setAssignmentTarget] = useState<AssignmentTarget | null>(null);

  useEffect(() => {
    if (!decodedCampaignId || !accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchCampaigns(accountId),
      fetchPhoneNumbers(accountId, { brandId: null, campaignId: decodedCampaignId, phoneNumberId: null })
    ])
      .then(([campaignResponse, phoneResponse]) => {
        if (cancelled) return;
        const nextCampaign = campaignResponse.items.find((item) => item.id === decodedCampaignId) ?? null;
        setCampaign(nextCampaign);
        setNumbers(phoneResponse.items);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, decodedCampaignId]);

  const detailsRows = useMemo(
    () => [
      { label: 'Campaign ID', value: campaign?.id ?? decodedCampaignId },
      { label: 'Name', value: campaign?.name ?? 'Unknown campaign' },
      { label: 'Account ID', value: accountId ?? '-' },
      { label: 'Phone numbers', value: String(numbers.length) }
    ],
    [accountId, campaign?.id, campaign?.name, decodedCampaignId, numbers.length]
  );

  return (
    <>
      <div className="page-pretitle">Messaging</div>
      <h2 className="page-title mb-3">{campaign?.name ?? 'Campaign detail'}</h2>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? <div className="text-secondary">Loading campaign details...</div> : null}

      {!loading ? (
        <>
          <div className="mb-3">
            <Link to="/campaigns" className="btn btn-sm btn-outline-secondary">
              Back to campaigns
            </Link>
          </div>

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'numbers' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveTab('numbers')}
              >
                Numbers
              </button>
            </li>
          </ul>

          {activeTab === 'details' ? (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title mb-0">Campaign metadata</h3>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    setAssignmentTarget({
                      scopeType: 'CAMPAIGN',
                      scopeId: decodedCampaignId,
                      title: `Assign templates - ${campaign?.name ?? decodedCampaignId}`
                    })
                  }
                >
                  Assign templates
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <tbody>
                    {detailsRows.map((row) => (
                      <tr key={row.label}>
                        <th className="w-25">{row.label}</th>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === 'numbers' ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title mb-0">Phone numbers</h3>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Number</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {numbers.map((number) => (
                      <tr key={number.id} className="cursor-pointer" onClick={() => setSelectedPhone(number)}>
                        <td>{number.label}</td>
                        <td>{number.e164}</td>
                        <td className="text-secondary">{number.id}</td>
                      </tr>
                    ))}
                    {!numbers.length ? (
                      <tr>
                        <td colSpan={3} className="text-secondary">
                          No phone numbers found for this campaign.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {selectedPhone ? (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Phone number detail</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedPhone(null)} />
              </div>
              <div className="modal-body">
                <dl className="row mb-0">
                  <dt className="col-sm-4">Label</dt>
                  <dd className="col-sm-8">{selectedPhone.label}</dd>
                  <dt className="col-sm-4">Number</dt>
                  <dd className="col-sm-8">{selectedPhone.e164}</dd>
                  <dt className="col-sm-4">Phone ID</dt>
                  <dd className="col-sm-8">{selectedPhone.id}</dd>
                  <dt className="col-sm-4">Campaign ID</dt>
                  <dd className="col-sm-8">{selectedPhone.campaignId}</dd>
                </dl>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setSelectedPhone(null)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary ms-auto"
                  onClick={() =>
                    setAssignmentTarget({
                      scopeType: 'PHONE',
                      scopeId: selectedPhone.e164,
                      campaignId: decodedCampaignId,
                      title: `Assign templates - ${selectedPhone.e164}`
                    })
                  }
                >
                  Assign templates
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <TemplateAssignmentModal
        isOpen={Boolean(assignmentTarget)}
        scopeType={assignmentTarget?.scopeType ?? 'ACCOUNT'}
        scopeId={assignmentTarget?.scopeId ?? accountId ?? ''}
        campaignId={assignmentTarget?.campaignId}
        title={assignmentTarget?.title ?? 'Assign templates'}
        onClose={() => setAssignmentTarget(null)}
      />
    </>
  );
}
